import { toDataURL } from 'qrcode';
import { wrapper } from './user-manager.js';
import { ApiClient } from './api-client.js';
import { SecureContents } from './secure-contents.js';
import { S3PresignedPost } from './s3-presigned-post.js';

export type RegisterViewInput = {
  form: HTMLFormElement;
  fileInput: HTMLInputElement;
  result: HTMLDivElement;
  anonyQRCodeImage: HTMLImageElement;
  anonyLink: HTMLAnchorElement;
  anonyLinkCopyButton: HTMLButtonElement;
  tokenParts: HTMLDivElement[];
  expiresLabel: HTMLSpanElement;
};

export async function registerView(input: RegisterViewInput) {
  const user = await wrapper.getUser();
  if (!user) {
    await wrapper.signinRedirect();
    return;
  }
  const url = new URL(window.location.href);
  history.replaceState({}, '', url.pathname);

  const { form, anonyLinkCopyButton } = input;
  anonyLinkCopyButton.addEventListener(
    'click',
    copyLinkToClipboard.bind(null, input),
  );
  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    setFormInputEnablement(input, false);
    form.ariaDisabled = 'true';
    forEachFormElement(form, (element) => {
      element.disabled = true;
    });
    try {
      const id = await uploadContents(input);
      if (!id) {
        return;
      }
      await renderResult(input, id[0], id[1]);
    } catch (err) {
      console.error('Error generating secure contents ID:', err);
      setFormInputEnablement(input, true);
      form.ariaDisabled = null;
      forEachFormElement(form, (element) => {
        element.disabled = false;
      });
      return;
    }
  });
}


function setFormInputEnablement(input: RegisterViewInput, enabled: boolean) {
  input.fileInput.disabled = !enabled;
  const button = input.form.querySelector('button[type="submit"]');
  if (button && 'disabled' in button) {
    button.disabled = !enabled;
  }
  if (enabled) {
    input.form.classList.remove('loading');
  } else {
    input.form.classList.add('loading');
  }
}

async function copyLinkToClipboard(input: RegisterViewInput) {
  const { anonyLink, anonyLinkCopyButton } = input;
  const i = anonyLinkCopyButton?.querySelector('i');

  try {
    await navigator.clipboard.writeText(anonyLink.href);

    i?.classList.remove('si-copy');
    i?.classList.add('si-check');

    anonyLinkCopyButton.disabled = true;
    setTimeout(() => {
      i?.classList.remove('si-check');
      i?.classList.add('si-copy');
      anonyLinkCopyButton.disabled = false;
    }, 2000);
  } catch (err) {
    console.error('Failed to copy link:', err);
  }
}

async function renderResult({
  form,
  result,
  anonyQRCodeImage,
  anonyLink,
  tokenParts,
  expiresLabel,
}: RegisterViewInput, key: string, anonyClientUrl: string) {
  result.style.display = 'block';
  form.style.display = 'none';

  const parts = key.split('-');
  for (let i = 0; i < tokenParts.length; i++) {
    if (i < parts.length) {
      tokenParts[i].textContent = parts[i];
      tokenParts[i].style.display = 'inline';
    } else {
      tokenParts[i].style.display = 'none';
    }
  }

  expiresLabel.textContent = 'Future';

  anonyLink.href = anonyClientUrl;
  anonyLink.textContent = anonyClientUrl.split('#')[1];

  const qrcode = await toDataURL(anonyClientUrl, {
    width: 256,
  });
  anonyQRCodeImage.src = qrcode;
}

async function uploadContents(input: RegisterViewInput) {
  const { fileInput, result } = input;
  if (!fileInput.files || fileInput.files.length === 0) {
    result.textContent = 'No file selected.';
    result.classList.add('error');
    return;
  }
  const file = fileInput.files[0];

  const apiClient = await ApiClient.fromConfigJson();
  const wordList = await apiClient.getWordList();
  const secureContents = await SecureContents.fromRandom(wordList);

  const upload = await secureContents.upload(file);
  const output = await apiClient.putDek(upload.secureContentsId, upload);
  if (output.ok) {
    const formData = output.value;
    const s3PresignedPost = new S3PresignedPost(formData.url, formData.fields);
    await s3PresignedPost.upload(upload.encryptedBlob);

    const key = secureContents.secureContentsId.readableKey;
    const anonyClientUrl = apiClient.anonyClientUrl(secureContents.secureContentsId.salt);
    return [key, anonyClientUrl] as const;
  } else if (output.error.type === 'authenticate') {
    await wrapper.signinRedirect();
  } else {
    throw new Error('Failed to upload DEK');
  }
}

function forEachFormElement(
  form: HTMLFormElement,
  callback: (element: HTMLInputElement) => void
) {
  for (let index = 0; index < form.elements.length; index++) {
    const element = form.elements[index];
    if (element instanceof HTMLInputElement && element.type !== 'submit') {
      callback(element);
    }
  }
}
